export type GraphData = { nodes: any[]; links: any[] };

export function normalizeGraphLink(link: any): { source: string; target: string; [key: string]: any } | null {
  const source = typeof link.source === 'string' ? link.source : link.source?.id;
  const target = typeof link.target === 'string' ? link.target : link.target?.id;
  if (!source || !target) return null;
  return {
    ...link,
    source,
    target,
  };
}

export function graphLinkKey(link: { source: string; target: string; [key: string]: any }): string {
  return `${link.source}-${link.target}-${link.connection_type || link.type || ''}-${link.timestamp || ''}`;
}

/** Merge full-dataset connections for expanded nodes onto the current filtered view. */
export function mergeExpandedGraphData(
  base: GraphData,
  originalNodes: any[],
  originalLinks: any[],
  expandedNodeIds: Set<string>,
  hiddenNodeIds: Set<string>
): GraphData {
  const nodeMap = new Map<string, any>();
  base.nodes.forEach((node) => nodeMap.set(node.id, node));
  originalNodes.forEach((node) => nodeMap.set(node.id, node));

  const linksMap = new Map<string, any>();
  base.links.forEach((link) => {
    const normalized = normalizeGraphLink(link);
    if (normalized) {
      linksMap.set(graphLinkKey(normalized), normalized);
    }
  });

  if (expandedNodeIds.size > 0) {
    originalLinks.forEach((link) => {
      const normalized = normalizeGraphLink(link);
      if (!normalized) return;
      if (expandedNodeIds.has(normalized.source) || expandedNodeIds.has(normalized.target)) {
        linksMap.set(graphLinkKey(normalized), normalized);
      }
    });
  }

  const nodeIds = new Set<string>();
  base.nodes.forEach((node) => nodeIds.add(node.id));
  if (expandedNodeIds.size > 0) {
    expandedNodeIds.forEach((id) => nodeIds.add(id));
    Array.from(linksMap.values()).forEach((link) => {
      if (expandedNodeIds.has(link.source) || expandedNodeIds.has(link.target)) {
        nodeIds.add(link.source);
        nodeIds.add(link.target);
      }
    });
  }

  const nodes = Array.from(nodeIds)
    .map((id) => nodeMap.get(id))
    .filter(Boolean);

  const visibleNodes = nodes.filter((node) => !hiddenNodeIds.has(node.id));
  const visibleNodeIds = new Set(visibleNodes.map((node) => node.id));
  const visibleLinks = Array.from(linksMap.values()).filter(
    (link) => visibleNodeIds.has(link.source) && visibleNodeIds.has(link.target)
  );

  return { nodes: visibleNodes, links: visibleLinks };
}

export function countGraphDegrees(nodes: any[], links: any[]): Map<string, number> {
  const degrees = new Map<string, number>();
  nodes.forEach((node) => degrees.set(node.id, 0));
  links.forEach((link) => {
    const normalized = normalizeGraphLink(link);
    if (!normalized) return;
    degrees.set(normalized.source, (degrees.get(normalized.source) || 0) + 1);
    degrees.set(normalized.target, (degrees.get(normalized.target) || 0) + 1);
  });
  return degrees;
}

const EXTERNAL_NODE_TYPES = new Set(['website', 'location', 'dwell', 'phone', 'app']);

function isDeviceLikeNodeType(nodeType: string | undefined): boolean {
  if (!nodeType) return false;
  return nodeType === 'device' || nodeType === 'entity' || nodeType === 'phone' || nodeType.startsWith('rf_');
}

/** Whether a node has additional connections worth exploring in the full dataset. */
export function isNodeExpandableInGraph(
  node: { id: string; type?: string },
  visibleDegrees: Map<string, number>,
  originalDegrees: Map<string, number>,
  originalLinks: any[],
  originalNodes: any[]
): boolean {
  const nodeType = node.type || 'unknown';
  const visibleDegree = visibleDegrees.get(node.id) || 0;
  const originalDegree = originalDegrees.get(node.id) || 0;

  if (originalDegree > visibleDegree) {
    return true;
  }

  if (isDeviceLikeNodeType(nodeType)) {
    return originalDegree >= 2;
  }

  if (!EXTERNAL_NODE_TYPES.has(nodeType)) {
    return originalDegree >= 2;
  }

  const nodeTypeMap = new Map<string, string>();
  originalNodes.forEach((n) => nodeTypeMap.set(n.id, n.type || 'unknown'));

  const connectedEntities = new Set<string>();
  originalLinks.forEach((link) => {
    const normalized = normalizeGraphLink(link);
    if (!normalized) return;
    const { source, target } = normalized;
    const sourceType = nodeTypeMap.get(source) || 'unknown';
    const targetType = nodeTypeMap.get(target) || 'unknown';

    if (source === node.id && isDeviceLikeNodeType(targetType)) {
      connectedEntities.add(target);
    }
    if (target === node.id && isDeviceLikeNodeType(sourceType)) {
      connectedEntities.add(source);
    }
  });

  return connectedEntities.size >= 2 || originalDegree >= 3;
}
